import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Save } from 'lucide-react'
import { toast } from 'sonner'
import {
  useCreateExecution,
  useCreateExecutionStep,
} from '@/features/executions/api/useExecutions'
import { useTestPlans } from '@/features/test-plans/api/useTestPlans'
import { useBuilds } from '@/features/test-plans/api/useBuilds'
import { usePlatforms } from '@/features/test-plans/api/usePlatforms'
import { useTestCases, useTestCaseSteps } from '@/features/test-cases/api/useTestCases'
import {
  ExecutionStatus,
  ExecutionType,

} from '@/types/execution.types'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

interface StepResult {
  tcstep_id: number
  status: ExecutionStatus
  notes: string
}

const RecordExecutionPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Pre-fill from URL params if available
  const [selectedTestPlanId, setSelectedTestPlanId] = useState<string>(
    searchParams.get('testplan') || ''
  )
  const [selectedBuildId, setSelectedBuildId] = useState<string>(
    searchParams.get('build') || ''
  )
  const [selectedTestCaseId, setSelectedTestCaseId] = useState<string>(
    searchParams.get('testcase') || ''
  )
  const [selectedPlatformId, setSelectedPlatformId] = useState<string>('')
  const [executionType, setExecutionType] = useState<ExecutionType>(
    ExecutionType.Manual
  )
  const [overallStatus, setOverallStatus] = useState<ExecutionStatus>(
    ExecutionStatus.NotRun
  )
  const [duration, setDuration] = useState<string>('')
  const [notes, setNotes] = useState('')
  const [stepResults, setStepResults] = useState<Record<number, StepResult>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Fetch reference data
  const { data: testPlans } = useTestPlans()
  const { data: builds } = useBuilds(
    selectedTestPlanId
      ? { testplan_id: parseInt(selectedTestPlanId, 10) }
      : {}
  )
  const { data: platforms } = usePlatforms()
  const { data: testCases } = useTestCases()
  const { data: testCaseSteps } = useTestCaseSteps(
    selectedTestCaseId ? parseInt(selectedTestCaseId, 10) : 0
  )

  const createExecution = useCreateExecution()
  const createStep = useCreateExecutionStep()

  // Initialize step results when test case steps load
  useEffect(() => {
    if (testCaseSteps && testCaseSteps.length > 0) {
      const initial: Record<number, StepResult> = {}
      testCaseSteps.forEach((step) => {
        initial[step.id] = {
          tcstep_id: step.id,
          status: ExecutionStatus.NotRun,
          notes: '',
        }
      })
      setStepResults(initial)
    }
  }, [testCaseSteps])

  // Reset build when test plan changes
  useEffect(() => {
    setSelectedBuildId('')
  }, [selectedTestPlanId])

  const handleStepStatusChange = (stepId: number, status: ExecutionStatus) => {
    setStepResults((prev) => ({
      ...prev,
      [stepId]: { ...prev[stepId], status },
    }))
  }

  const handleStepNotesChange = (stepId: number, value: string) => {
    setStepResults((prev) => ({
      ...prev,
      [stepId]: { ...prev[stepId], notes: value },
    }))
  }

  // POST /api/executions + POST /api/executions/{id}/steps
  const handleSubmit = async () => {
    if (!selectedTestPlanId || !selectedBuildId || !selectedTestCaseId || !overallStatus) {
      toast.error('Please fill in all required fields')
      return
    }

    setIsSubmitting(true)
    try {
      // Create the execution
      const execution = await createExecution.mutateAsync({
        build_id: parseInt(selectedBuildId, 10),
        tcversion_id: parseInt(selectedTestCaseId, 10),
        testplan_id: parseInt(selectedTestPlanId, 10),
        platform_id: selectedPlatformId
          ? parseInt(selectedPlatformId, 10)
          : undefined,
        status: overallStatus,
        notes: notes.trim() || undefined,
        duration: duration ? parseInt(duration, 10) : undefined,
        execution_type: executionType,
      })

      // Create step results for any step that was actually recorded
      if (testCaseSteps && testCaseSteps.length > 0) {
        for (const step of testCaseSteps) {
          const result = stepResults[step.id]
          if (result && result.status !== ExecutionStatus.NotRun) {
            await createStep.mutateAsync({
              executionId: execution.id,
              data: {
                tcstep_id: step.id,
                status: result.status,
                notes: result.notes.trim() || undefined,
              },
            })
          }
        }
      }

      toast.success('Execution recorded successfully')
      navigate(`/executions/test-executions/${execution.id}`)
    } catch (err) {
      toast.error('Failed to record execution')
    } finally {
      setIsSubmitting(false)
    }
  }

  const testPlanList = Array.isArray(testPlans)
    ? testPlans
    : (testPlans as any)?.testplans || []
  const buildList = Array.isArray(builds)
    ? builds
    : (builds as any)?.builds || []
  const platformList = Array.isArray(platforms)
    ? platforms
    : (platforms as any)?.platforms || []
  const testCaseList = Array.isArray(testCases)
    ? testCases
    : (testCases as any)?.testcases || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/executions/test-executions')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Record Execution</h1>
          <p className="text-muted-foreground">
            Record manual test execution results step-by-step
          </p>
        </div>
      </div>

      {/* Execution Context */}
      <Card>
        <CardHeader>
          <CardTitle>Execution Context</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Test Plan */}
            <div className="space-y-2">
              <Label>Test Plan *</Label>
              <Select value={selectedTestPlanId} onValueChange={setSelectedTestPlanId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select test plan" />
                </SelectTrigger>
                <SelectContent>
                  {testPlanList.map((tp: any) => (
                    <SelectItem key={tp.id} value={tp.id.toString()}>
                      {tp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Build */}
            <div className="space-y-2">
              <Label>Build *</Label>
              <Select
                value={selectedBuildId}
                onValueChange={setSelectedBuildId}
                disabled={!selectedTestPlanId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={selectedTestPlanId ? 'Select build' : 'Select a test plan first'} />
                </SelectTrigger>
                <SelectContent>
                  {buildList.map((b: any) => (
                    <SelectItem key={b.id} value={b.id.toString()}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Test Case */}
            <div className="space-y-2">
              <Label>Test Case *</Label>
              <Select value={selectedTestCaseId} onValueChange={setSelectedTestCaseId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select test case" />
                </SelectTrigger>
                <SelectContent>
                  {testCaseList.map((tc: any) => (
                    <SelectItem key={tc.id} value={tc.id.toString()}>
                      {tc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Platform */}
            <div className="space-y-2">
              <Label>Platform</Label>
              <Select value={selectedPlatformId} onValueChange={setSelectedPlatformId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select platform (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {platformList.map((p: any) => (
                    <SelectItem key={p.id} value={p.id.toString()}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Execution Type */}
            <div className="space-y-2">
              <Label>Execution Type</Label>
              <Select
                value={executionType.toString()}
                onValueChange={(v) => setExecutionType(parseInt(v, 10) as ExecutionType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Manual</SelectItem>
                  <SelectItem value="2">Automated</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Overall Status */}
            <div className="space-y-2">
              <Label>Overall Status *</Label>
              <Select
                value={overallStatus}
                onValueChange={(v) => setOverallStatus(v as ExecutionStatus)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ExecutionStatus.Passed}>Passed</SelectItem>
                  <SelectItem value={ExecutionStatus.Failed}>Failed</SelectItem>
                  <SelectItem value={ExecutionStatus.Blocked}>Blocked</SelectItem>
                  <SelectItem value={ExecutionStatus.NotRun}>Not Run</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Duration */}
            <div className="space-y-2">
              <Label>Duration (seconds)</Label>
              <Input
                type="number"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                placeholder="e.g. 120"
                min="0"
              />
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Overall execution notes..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Step-by-Step Results */}
      {testCaseSteps && testCaseSteps.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Step Results ({testCaseSteps.length} steps)</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60px]">Step</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Expected Result</TableHead>
                  <TableHead className="w-[150px]">Status</TableHead>
                  <TableHead className="w-[200px]">Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {testCaseSteps.map((step) => (
                  <TableRow key={step.id}>
                    <TableCell className="font-mono">
                      {step.step_number}
                    </TableCell>
                    <TableCell className="whitespace-pre-wrap">
                      {step.actions || '-'}
                    </TableCell>
                    <TableCell className="whitespace-pre-wrap">
                      {step.expected_results || '-'}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={stepResults[step.id]?.status || ExecutionStatus.NotRun}
                        onValueChange={(v) =>
                          handleStepStatusChange(step.id, v as ExecutionStatus)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={ExecutionStatus.Passed}>Passed</SelectItem>
                          <SelectItem value={ExecutionStatus.Failed}>Failed</SelectItem>
                          <SelectItem value={ExecutionStatus.Blocked}>Blocked</SelectItem>
                          <SelectItem value={ExecutionStatus.NotRun}>Not Run</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Textarea
                        value={stepResults[step.id]?.notes || ''}
                        onChange={(e) =>
                          handleStepNotesChange(step.id, e.target.value)
                        }
                        placeholder="Step notes..."
                        rows={1}
                        className="min-h-[36px]"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Submit */}
      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={() => navigate('/executions/test-executions')}
        >
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isSubmitting}>
          <Save className="mr-2 h-4 w-4" />
          {isSubmitting ? 'Recording...' : 'Record Execution'}
        </Button>
      </div>
    </div>
  )
}

export default RecordExecutionPage
